import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BrainChatMessage } from "@/types/brain/brain";
import BrainLink from "./BrainLink";
import ToolCallIndicator from "./ToolCallIndicator";
import MessageActions from "./MessageActions";
import RetryButton from "./RetryButton";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { formatExactTime } from "@/components/agent/display/utils/formatters";

const USER_MESSAGE_COLLAPSE_THRESHOLD = 280;

const UserMessageContent = ({ content }: { content: string }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = content.length > USER_MESSAGE_COLLAPSE_THRESHOLD;

  if (!isLong) {
    return <p className="text-base whitespace-pre-wrap">{content}</p>;
  }

  return (
    <div>
      <p
        className={cn(
          "text-base whitespace-pre-wrap",
          !expanded && "line-clamp-6",
        )}
      >
        {content}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-1.5 text-xs font-medium text-white/75 hover:text-white underline underline-offset-2"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
};

const MASCOT_SRC = "/genie-mascots/role/support.png";

type IntermediateMessage = NonNullable<BrainChatMessage["intermediate_messages"]>[number];

const mdComponents = {
  p: ({ children }: { children: React.ReactNode }) => <p className="mb-1 leading-relaxed last:mb-0 text-xs">{children}</p>,
  ul: ({ children }: { children: React.ReactNode }) => <ul className="mb-1 pl-3 space-y-0.5 list-disc last:mb-0">{children}</ul>,
  ol: ({ children }: { children: React.ReactNode }) => <ol className="mb-1 pl-3 space-y-0.5 list-decimal last:mb-0">{children}</ol>,
  li: ({ children }: { children: React.ReactNode }) => <li className="text-xs">{children}</li>,
  strong: ({ children }: { children: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
  code: ({ children }: { children: React.ReactNode }) => <code className="bg-black/5 px-1 py-0.5 rounded text-[11px] font-mono">{children}</code>,
};

const IntermediateMessagesToggle = ({
  messages,
  isMobile,
}: {
  messages: IntermediateMessage[];
  isMobile?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {open ? "Hide" : "Show"} thinking ({messages.length} step{messages.length !== 1 ? "s" : ""})
      </button>

      {open && (
        <div className={cn("mt-2 space-y-2 border-l-2 border-gray-200 pl-3", isMobile ? "text-[11px]" : "text-xs")}>
          {messages.map((msg) => (
            <div key={msg.iteration} className="space-y-1">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                Step {msg.iteration}
              </span>
              {msg.content && (
                <div className="text-gray-600">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {msg.content}
                  </ReactMarkdown>
                </div>
              )}
              {msg.tool_calls && msg.tool_calls.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {msg.tool_calls.map((tc, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-gray-200 text-gray-500 rounded px-1.5 py-0.5">
                      {tc.tool}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/** Sanitize legacy tool call summaries that still contain raw API errors/JSON. */
const sanitizeLegacySummary = (summary: string): string => {
  if (/^Error: API \d+/.test(summary) || /^\{.*"error"/.test(summary)) {
    const lower = summary.toLowerCase();
    if (lower.includes("not_found") || lower.includes("404")) return "Couldn't find that";
    if (lower.includes("unauthorized") || lower.includes("403") || lower.includes("401")) return "Ran into a permissions issue";
    return "Hit a small snag";
  }
  return summary;
};

interface ChatMessageProps {
  message: BrainChatMessage;
  userAvatarUrl?: string;
  userName?: string;
  onRetry?: () => void;
  isLastFailed?: boolean;
  onFeedback?: (messageId: string, rating: "positive" | "negative") => void;
  isMobile?: boolean;
}

const ChatMessage = ({
  message,
  userAvatarUrl,
  userName,
  onRetry,
  isLastFailed,
  onFeedback,
  isMobile,
}: ChatMessageProps) => {
  const isUser = message.role === "user";
  const initials = userName
    ? userName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
    : "U";

  return (
    <div className={cn("group", isUser ? "flex flex-col items-end" : "flex flex-col items-start")}>
      <div className={cn("flex items-start gap-2.5", isUser ? "justify-end" : "justify-start")}>
        {/* Genie mascot avatar (left side for assistant) */}
        {!isUser && (
          <img
            src={MASCOT_SRC}
            className={cn(
              "rounded-full object-cover ring-1 ring-primary/20 flex-shrink-0 mt-0.5",
              isMobile ? "h-8 w-8" : "h-10 w-10",
            )}
            alt="Help Genie"
          />
        )}

        <div
          className={cn(
            "relative rounded-lg px-4 py-2.5",
            isMobile ? "max-w-[92%]" : "max-w-[85%]",
            isUser
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-900",
          )}
        >
          {!isUser && message.intermediate_messages && message.intermediate_messages.length > 0 && (
            <IntermediateMessagesToggle
              messages={message.intermediate_messages}
              isMobile={isMobile}
            />
          )}
          {isUser ? (
            <UserMessageContent content={message.content} />
          ) : (
            <div className="text-base max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h3 className="text-base font-bold mt-2.5 mb-1 text-gray-900">{children}</h3>,
                  h2: ({ children }) => <h3 className="text-base font-bold mt-2.5 mb-1 text-gray-900">{children}</h3>,
                  h3: ({ children }) => <h4 className="text-base font-semibold mt-2 mb-0.5 text-gray-900">{children}</h4>,
                  p: ({ children }) => <p className="mb-1.5 leading-relaxed last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="mb-1.5 pl-4 space-y-0.5 list-disc last:mb-0">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-1.5 pl-4 space-y-0.5 list-decimal last:mb-0">{children}</ol>,
                  li: ({ children }) => <li className="text-base">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  hr: () => <hr className="my-2 border-gray-200" />,
                  code: ({ children }) => <code className="bg-black/5 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                  pre: ({ children }) => <pre className="bg-black/5 p-2 rounded overflow-x-auto mb-1.5 text-xs">{children}</pre>,
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-300 pl-2 italic text-gray-600 mb-1.5">{children}</blockquote>,
                  table: ({ children }) => <table className="text-sm border-collapse w-full mb-1.5">{children}</table>,
                  th: ({ children }) => <th className="border border-gray-300 px-2 py-1 bg-gray-200 font-semibold text-left">{children}</th>,
                  td: ({ children }) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
                  a: ({ children, href }) => <BrainLink href={href}>{children}</BrainLink>,
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {message.tool_calls && message.tool_calls.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.tool_calls.map((tc, i) => (
                <ToolCallIndicator key={i} tool={tc.tool} summary={sanitizeLegacySummary(tc.summary)} />
              ))}
            </div>
          )}
        </div>

        {/* User avatar (right side for user messages) */}
        {isUser && (
          <Avatar className={cn("flex-shrink-0 mt-0.5", isMobile ? "h-8 w-8" : "h-10 w-10")}>
            {userAvatarUrl ? (
              <AvatarImage src={userAvatarUrl} alt={userName || "You"} />
            ) : null}
            <AvatarFallback
              className="text-xs font-bold text-white"
              style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              {initials}
            </AvatarFallback>
          </Avatar>
        )}
      </div>

      {/* Timestamp */}
      <span
        className={cn(
          "text-[11px] text-gray-400 mt-1",
          isUser ? "pr-12 sm:pr-14" : "pl-12 sm:pl-14",
        )}
      >
        {formatExactTime(message.created_at)}
      </span>

      {/* Assistant actions: copy + feedback */}
      {!isUser && !message.id.startsWith("temp-") && (
        <div className={cn("pl-12 sm:pl-14")}>
          <MessageActions
            content={message.content}
            messageId={message.id}
            feedback={message.feedback}
            onFeedback={onFeedback}
            isMobile={isMobile}
          />
        </div>
      )}

      {/* Retry button on failed user messages */}
      {isUser && isLastFailed && onRetry && (
        <div className="pr-12 sm:pr-14">
          <RetryButton onRetry={onRetry} />
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
